// ============================================================
// Chess Arena — Wave System
// ============================================================
// Manages enemy wave spawns based on elapsed match time.
// Reads the `waves` array from LevelConfig and spawns enemy
// pieces when each wave's trigger time is reached.
// ============================================================

import {
  GridPos,
  PieceType,
  Team,
  WaveConfig,
  LevelConfig,
  BOARD_COLS,
} from '../core/types';
import eventBus from '../core/eventBus';

// -----------------------------------------------------------------
// Callback for placing pieces — same contract as SpawnSystem
// -----------------------------------------------------------------

export interface WaveCallbacks {
  isCellAvailable: (pos: GridPos) => boolean;
  placePiece: (type: PieceType, team: Team, pos: GridPos) => string;
}

// -----------------------------------------------------------------
// Internal tracking per wave
// -----------------------------------------------------------------

/** Seconds before wave trigger to show the warning. */
const WARN_LEAD_SEC = 2;

interface WaveState {
  config: WaveConfig;
  triggered: boolean;
  warned: boolean;
}

// -----------------------------------------------------------------
// WaveSystem
// -----------------------------------------------------------------

export class WaveSystem {
  private scene: Phaser.Scene;
  private waves: WaveState[] = [];
  private callbacks!: WaveCallbacks;
  private active = false;

  // Enemy King visibility
  private enemyKingVisible = true;
  private enemyKingAppearsAfterWave: number | null = null;
  private waveIndex = 0; // 1-based count of triggered waves

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  init(levelConfig: LevelConfig, callbacks: WaveCallbacks): void {
    this.callbacks = callbacks;
    this.waves = (levelConfig.waves || []).map((w) => ({
      config: { ...w, pieces: { ...w.pieces } },
      triggered: false,
      warned: false,
    }));
    this.waveIndex = 0;
    this.enemyKingVisible = levelConfig.enemyKingVisible;
    this.enemyKingAppearsAfterWave = levelConfig.enemyKingAppearsAfterWave;
    this.active = true;
  }

  reset(): void {
    this.active = false;
    this.waves = [];
    this.waveIndex = 0;
  }

  // ------------------------------------------------------------------
  // Frame update — called with elapsed match time in seconds
  // ------------------------------------------------------------------

  update(elapsedSec: number): void {
    if (!this.active) return;

    for (const wave of this.waves) {
      if (wave.triggered) continue;

      // Show warning before the wave arrives
      const warnTime = wave.config.triggerTimeSec - WARN_LEAD_SEC;
      if (!wave.warned && elapsedSec >= warnTime) {
        wave.warned = true;
        this.showIncomingWaveWarning();
      }

      if (elapsedSec >= wave.config.triggerTimeSec) {
        wave.triggered = true;
        this.waveIndex++;
        this.triggerWave(wave.config);

        // Check if enemy King should appear after this wave
        if (
          !this.enemyKingVisible &&
          this.enemyKingAppearsAfterWave !== null &&
          this.waveIndex >= this.enemyKingAppearsAfterWave
        ) {
          this.spawnEnemyKing();
          this.enemyKingVisible = true;
        }
      }
    }
  }

  // ------------------------------------------------------------------
  // Wave execution
  // ------------------------------------------------------------------

  private triggerWave(wave: WaveConfig): void {
    const piecesToSpawn: PieceType[] = [];

    for (const [type, count] of Object.entries(wave.pieces)) {
      for (let i = 0; i < (count ?? 0); i++) {
        piecesToSpawn.push(type as PieceType);
      }
    }

    for (const pieceType of piecesToSpawn) {
      const cell = this.findEnemySpawnCell();
      if (cell) {
        const id = this.callbacks.placePiece(pieceType, 'enemy', cell);
        this.playWaveSpawnEffect(cell);

        eventBus.emit('piece_spawned', {
          pieceId: id,
          type: pieceType,
          team: 'enemy',
          pos: { col: cell.col, row: cell.row },
          fromWave: true,
        });
      }
    }

    eventBus.emit('wave_triggered', {
      waveIndex: this.waveIndex,
      triggerTimeSec: wave.triggerTimeSec,
    });
  }

  private spawnEnemyKing(): void {
    const cell = this.findEnemySpawnCell();
    if (cell) {
      const id = this.callbacks.placePiece('King', 'enemy', cell);
      this.playWaveSpawnEffect(cell);

      eventBus.emit('piece_spawned', {
        pieceId: id,
        type: 'King',
        team: 'enemy',
        pos: { col: cell.col, row: cell.row },
        fromWave: true,
        kingReveal: true,
      });
    }
  }

  private showIncomingWaveWarning(): void {
    const { width, height } = this.scene.cameras.main;
    const x = width / 2;
    const y = height / 2;

    const label = this.scene.add.text(x, y, 'Incoming Wave!', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '28px',
      color: '#ff4444',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    });
    label.setOrigin(0.5);
    label.setDepth(100);
    label.setAlpha(0);

    // Blink 3 times then destroy
    const BLINK_ON = 200;
    const BLINK_OFF = 100;

    this.scene.tweens.add({
      targets: label,
      alpha: { from: 0, to: 1 },
      duration: BLINK_ON,
      yoyo: true,
      hold: BLINK_ON,
      repeatDelay: BLINK_OFF,
      repeat: 2,
      onComplete: () => label.destroy(),
    });
  }

  private playWaveSpawnEffect(pos: GridPos): void {
    const cellSize = 80;
    const x = pos.col * cellSize + cellSize / 2;
    const y = pos.row * cellSize + cellSize / 2;

    const flash = this.scene.add.circle(x, y, cellSize * 0.45, 0xff4444, 0.6);
    flash.setDepth(10);

    this.scene.tweens.add({
      targets: flash,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 350,
      ease: 'Quad.easeOut',
      onComplete: () => flash.destroy(),
    });
  }

  // ------------------------------------------------------------------
  // Cell selection — enemy back rows (0-1)
  // ------------------------------------------------------------------

  private findEnemySpawnCell(): GridPos | null {
    const candidates: GridPos[] = [];

    for (const row of [0, 1]) {
      for (let col = 0; col < BOARD_COLS; col++) {
        const pos: GridPos = { col, row };
        if (this.callbacks.isCellAvailable(pos)) {
          candidates.push(pos);
        }
      }
    }

    if (candidates.length === 0) return null;
    return candidates[Phaser.Math.Between(0, candidates.length - 1)];
  }

  // ------------------------------------------------------------------
  // Queries
  // ------------------------------------------------------------------

  /** How many waves have been triggered so far. */
  getTriggeredWaveCount(): number {
    return this.waveIndex;
  }

  /** Total number of waves configured for this level. */
  getTotalWaveCount(): number {
    return this.waves.length;
  }

  /** Whether the enemy King is currently visible / has appeared. */
  isEnemyKingVisible(): boolean {
    return this.enemyKingVisible;
  }
}
